/**
 * Aspect-web overlay.
 *
 * Builds board inputs for the "aspect web" visualization: a small label
 * node per string aspect (rendered with the distinct `arch-aspect`
 * widget, NOT a default module card), and a subtle coloured `member →
 * group` edge from each module that carries the aspect. Bare references
 * reuse the real target module as the group — no synthetic node, just an
 * edge to it.
 *
 * Pure over the wire `Module[]`, so the app can recompute it reactively
 * as the user selects which labels.keys to web. Pass `keys` to restrict
 * to chosen label keys (the web of *all* aspects at once is noise).
 *
 * Only labels declared directly on a module form the web; interface /
 * surface labels describe a surface, not a node.
 */

import type { DependencyEdge, Module, StableId } from "@archlang/scene/arch-model";
import { moduleKey } from "@archlang/scene/arch-model";
import { aspectColorFor } from "./aspectColors.js";

/** Tag of the small-pill custom element that renders an aspect node. */
export const ASPECT_WIDGET_TAG = "arch-aspect";

export interface AspectOverlay {
  readonly modules: readonly Module[];
  readonly edges: readonly DependencyEdge[];
  /**
   * Synthetic-node id → container module key (flatten's `parentId` keying).
   * An aspect place belongs where its members live: a hub is parented to the
   * DEEPEST COMMON ANCESTOR of its members, a replicated copy to its member's
   * own parent — so the pill renders inside the subspace beside the things
   * that carry the aspect instead of orphaned at the root. No entry = root
   * (members at the root, or spanning containers with no common ancestor).
   */
  readonly parentByNodeId: ReadonlyMap<string, string>;
}

/**
 * Fold {@link AspectOverlay.parentByNodeId} into a flattened board input:
 * re-parent each synthetic node whose recorded container is PRESENT in the
 * flat set (a view projection may have hidden it — then the pill stays a
 * root node rather than orphan). Untouched entries keep their identity.
 */
export function applyAspectParents<T extends Module & { parentId?: StableId }>(
  flat: readonly T[],
  parentByNodeId: ReadonlyMap<string, string>,
): T[] {
  if (parentByNodeId.size === 0) return [...flat];
  const present = new Set(flat.map((m) => moduleKey(m)));
  return flat.map((m) => {
    const p = m.id ? parentByNodeId.get(String(m.id)) : undefined;
    return p !== undefined && present.has(p) ? { ...m, parentId: p as StableId } : m;
  });
}

export interface AspectOverlayOptions {
  /** Restrict to these dotted label keys. Undefined = every key. */
  readonly keys?: ReadonlySet<string>;
  /**
   * Keys (⊆ {@link keys}) rendered in `replicated` mode: a PRIVATE duplicate
   * of the place node beside each consuming member (draw.io style), instead of
   * one shared hub with N spokes. Every copy carries the place's `logicalId`,
   * so selection / hover / focus fan out to all copies and legend / minimap
   * dedupe to one. Absent (or not in this set) → the default `web` hub.
   */
  readonly replicatedKeys?: ReadonlySet<string>;
}

interface IndexEntry {
  readonly mod: Module;
  readonly path: string[];
  /** v0.11: dotted landed space (inherited from the root when a nested
   *  module doesn't carry its own) — folds into edge endpoints so the
   *  overlay's edge↔node join matches the spaced moduleKey. */
  readonly space?: string;
}

interface Atom {
  readonly kind: "string" | "identifier";
  readonly text: string;
}

export function buildAspectOverlay(
  roots: readonly Module[],
  opts: AspectOverlayOptions = {}
): AspectOverlay {
  const byShortName = new Map<string, IndexEntry>();
  indexModules(roots, [], byShortName);

  const nodes = new Map<string, Module>();
  const edges: DependencyEdge[] = [];
  const seenEdge = new Set<string>();
  const { keys, replicatedKeys } = opts;

  // Where each synthetic place belongs (see AspectOverlay.parentByNodeId):
  // replicated copies pin their member's parent directly; hubs accumulate the
  // longest common ANCESTOR-KEY prefix across their members' parent chains.
  const parentByNodeId = new Map<string, string>();
  const hubChain = new Map<string, string[]>();

  const addEdge = (e: DependencyEdge): void => {
    const k = `${e.fromModuleId ?? e.fromModulePath.join(".")}->${e.toModuleId ?? e.toModulePath.join(".")}`;
    if (seenEdge.has(k)) return;
    seenEdge.add(k);
    edges.push(e);
  };

  const walk = (
    mods: readonly Module[],
    parent: string[],
    chain: readonly string[],
    parentSpace?: string
  ): void => {
    for (const m of mods) {
      const path = [...parent, m.name];
      const mSpace = m.space ?? parentSpace;
      const memberKey = m.id ?? (mSpace ? `${mSpace}::${path.join(".")}` : path.join("."));
      for (const label of m.aspects) {
        const key = label.path.join(".");
        if (keys && !keys.has(key)) continue;
        const replicated = replicatedKeys?.has(key) ?? false;
        for (const atom of atoms(label.value)) {
          // The place's canonical logical id: an implicit place for a
          // classification, the real target module for a membership ref.
          let logicalId: string;
          let label2: string;
          if (atom.kind === "string") {
            logicalId = `aspect:${key}=${JSON.stringify(atom.text)}`;
            label2 = atom.text;
          } else {
            const target = byShortName.get(atom.text);
            if (!target) continue; // unresolved bare ref — ASPECT_BAD_REF covers it
            logicalId = String(target.mod.id ?? target.path.join("."));
            label2 = atom.text;
          }
          const color = aspectColorFor(label2);
          if (replicated) {
            // One private copy of the place beside THIS member (no shared hub).
            const copyId = `${logicalId}::rep::${memberKey}`;
            if (!nodes.has(copyId))
              nodes.set(copyId, synthAspectModule(copyId, label2, key, color, logicalId));
            const parentKey = chain[chain.length - 1];
            if (parentKey !== undefined) parentByNodeId.set(copyId, parentKey);
            addEdge(aspectEdge(m.id, path, [copyId], copyId as StableId, color, true, mSpace));
          } else if (atom.kind === "string") {
            // Shared implicit-place hub + spoke (the web).
            if (!nodes.has(logicalId))
              nodes.set(logicalId, synthAspectModule(logicalId, label2, key, color));
            const prev = hubChain.get(logicalId);
            hubChain.set(logicalId, prev ? commonPrefix(prev, chain) : [...chain]);
            addEdge(aspectEdge(m.id, path, [logicalId], logicalId as StableId, color, false, mSpace));
          } else {
            // Membership web: spoke to the REAL target module (no synth node).
            const target = byShortName.get(atom.text)!;
            addEdge(aspectEdge(m.id, path, target.path, target.mod.id, color, false, mSpace, target.space));
          }
        }
      }
      walk(m.children, path, [...chain, moduleKey(m)], mSpace);
    }
  };
  walk(roots, [], []);

  for (const [logicalId, chain] of hubChain) {
    const deepest = chain[chain.length - 1];
    if (deepest !== undefined) parentByNodeId.set(logicalId, deepest);
  }

  return { modules: [...nodes.values()], edges, parentByNodeId };
}

/** Longest common prefix of two ancestor-key chains. */
function commonPrefix(a: readonly string[], b: readonly string[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < a.length && i < b.length && a[i] === b[i]; i++) out.push(a[i]!);
  return out;
}

/** A serialized label value is the raw core `Value`. Flatten lists. */
function atoms(value: unknown): Atom[] {
  if (!value || typeof value !== "object") return [];
  const v = value as { kind?: string; text?: string; items?: unknown[] };
  if (v.kind === "list") return (v.items ?? []).flatMap(atoms);
  if ((v.kind === "string" || v.kind === "identifier") && typeof v.text === "string") {
    return [{ kind: v.kind, text: v.text }];
  }
  return [];
}

function aspectEdge(
  fromId: StableId | undefined,
  fromPath: string[],
  toPath: string[],
  toId: StableId | undefined,
  color: string,
  stub = false,
  fromSpace?: string,
  toSpace?: string
): DependencyEdge {
  return {
    fromModulePath: fromPath,
    fromModuleId: fromId,
    toModulePath: toPath,
    toModuleId: toId,
    toInterfacePath: [],
    source: "aspect",
    aspectColor: color,
    ...(stub ? { aspectStub: true } : {}),
    // v0.11 (D2a): landed endpoint spaces keep the overlay's edge keys
    // aligned with the spaced moduleKey (spaceless models unchanged).
    ...(fromSpace ? { fromSpace } : {}),
    ...(toSpace ? { toSpace } : {}),
  };
}

/** A synthetic aspect place-node (the small `arch-aspect` pill). `logicalId`,
 *  set only on a `replicated` copy, marks it a duplicate of one logical place. */
function synthAspectModule(
  id: string,
  value: string,
  key: string,
  color: string,
  logicalId?: string
): Module {
  return {
    id: id as StableId,
    name: value,
    typeName: "aspect",
    baseType: "module",
    description: `${key} = ${value}`,
    fields: [
      { path: ["widget"], value: { kind: "identifier", text: ASPECT_WIDGET_TAG } },
      { path: ["widget", "color"], value: { kind: "string", text: color } },
    ],
    ...(logicalId ? { logicalId } : {}),
    aspects: [],
    surfaces: [],
    interfaces: [],
    children: [],
  };
}

function indexModules(
  mods: readonly Module[],
  parent: string[],
  byShortName: Map<string, IndexEntry>,
  parentSpace?: string
): void {
  for (const m of mods) {
    const path = [...parent, m.name];
    const space = m.space ?? parentSpace;
    if (!byShortName.has(m.name)) byShortName.set(m.name, { mod: m, path, space });
    indexModules(m.children, path, byShortName, space);
  }
}
